<script lang="ts">
    import { onMount } from 'svelte';
    import { createEventDispatcher } from 'svelte';
    import { _ } from 'svelte-i18n';

    import type { ZoneType } from './types';
    import IconRemove from '../../shared/Icons/IconRemove.svelte';
    import InputText from '../../shared/InputText.svelte';
    import SelectVehicle from '../Vehicles/SelectVehicle.svelte';
    import type { VehicleResponseType } from '../Vehicles/types';
    import StyleLikeFilterItem from '../../shared/StyleLikeFilterItem.svelte';
    import { debug, tacticalPlanRuleCurrent, tacticalPlanStore } from '../../../stores/store';
    import CustomColorPicker from '../../shared/CustomColorPicker.svelte';
    import InputTextInline from '../../shared/InputTextInline.svelte';
    import { waitForZoneSave } from '../TacticalPlan/waitForZoneSave';
    import type { TacticalPlanRule } from '../TacticalPlan/TacticalPlanRule';
    import ModalConfirm from '../../shared/Modals/ModalConfirm.svelte';
    import { openModal } from 'svelte-modals';
    import ModalRemoveSomething from '../Modals/ModalRemoveSomething.svelte';
    import IconRemoveFilter from '../../shared/Icons/IconRemoveFilter.svelte';
    import { ZoneListErrorTypes } from './ZoneListTypes';
    import IconError from '../../shared/Icons/IconError.svelte';
    import type { ValidatorReport, ValidatorReportItemType } from '../TacticalPlan/ValidatorReport';
    import type { ZoneEditor } from './ZoneEditor';

    // https://coloris.js.org/
    // https://www.npmjs.com/package/@melloware/coloris
    // import "@melloware/coloris/dist/coloris.css";
    // import Coloris from "@melloware/coloris";
    // import * as Color from "@melloware/coloris";

    let dispatch = createEventDispatcher();

    export let zone: ZoneType | null = null;
    export let vehicles: VehicleResponseType[] = [];
    export let readOnly: boolean = false;
    export let alreadyUsedVehicles: number[] = [];
    export let rule: TacticalPlanRule;
    export let tacticalPlanValidatorReport: ValidatorReport;
    export let zoneEditor: ZoneEditor;

    let onZoneHover: boolean = false;
    let zoneListErrors: ValidatorReportItemType[] = [];

    function onRemoveZone(zone_id: number | null) {
        if (zone_id === null) {
            return;
        }
        dispatch('remove', { zone_id: zone_id });
    }

    function onChangeZone(zone: ZoneType) {
        console.debug('onChangeZone', zone);
        dispatch('change', { zone: zone });
    }

    $: {
        if (zone?.id) {
            zoneListErrors = tacticalPlanValidatorReport.getMessagesForZoneId(zone.id.toString());
        }
    }
</script>

<div class="zone-item zone-item-data zone-id-{zone?.id || 'null'}">
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div
        class="vehicle"
        style="display: flex; align-items: center; column-gap: 1px;"
        on:mouseenter={() => (onZoneHover = true)}
        on:mouseleave={() => (onZoneHover = false)}
        on:click={()=>{
            if (!readOnly) {
                console.log('Zoom to zone on click')
                if (zone) zoneEditor.zoomToZone(zone.id);
            }
        }}
    >
        <StyleLikeFilterItem mode="standard">
            <div
                style="display: flex; column-gap: 6px; align-items: center; padding: 0 0 0 6px;"
                on:click={() => {
                    if (readOnly) {
                        waitForZoneSave(() => {
                            readOnly = false;
                            $tacticalPlanRuleCurrent = rule.getInnerId();
                        });
                    }
                }}
            >
                {#if zone}
                    <CustomColorPicker
                        bind:color={zone.color}
                        disabled={readOnly}
                        onChange={() => onChangeZone(zone)}
                    />
                    <div class="zone-name">
                        <InputTextInline
                            bind:value={zone.name}
                            disabled={readOnly}
                            editMode={false}
                            showEditIcon={false}
                            styleMode="black"
                            onChange={v => {
                                onChangeZone(zone);
                            }}
                        />
                    </div>
                    <StyleLikeFilterItem mode="vehicle">
                        <SelectVehicle
                            disabled={readOnly}
                            selected={zone.vehicle_id}
                            {alreadyUsedVehicles}
                            onChange={selected => {
                                $tacticalPlanRuleCurrent = rule.getInnerId();
                                alreadyUsedVehicles = alreadyUsedVehicles.filter(
                                    v => v !== zone.vehicle_id
                                );
                                if (selected !== null) {
                                    alreadyUsedVehicles.push(selected);
                                    alreadyUsedVehicles = alreadyUsedVehicles;
                                }
                                zone.vehicle_id = selected;
                                onChangeZone(zone);
                                return true;
                            }}
                        />
                    </StyleLikeFilterItem>
                {/if}
            </div>
        </StyleLikeFilterItem>
        {#if zone && zone.id !== null && onZoneHover && !readOnly}
            <IconRemoveFilter
                on:click={() => {
                    openModal(ModalRemoveSomething, {
                        name: zone?.name || '',
                        text: $_('do_you_want_to_remove_zone'),
                        onClick: () => {
                            if (zone.vehicle_id !== null)
                                alreadyUsedVehicles = alreadyUsedVehicles.filter(
                                    v => v !== zone.vehicle_id
                                );
                            onRemoveZone(zone.id);
                        },
                    });
                }}
            />
        {/if}
        <div style="display: flex; align-items: center;">
            {#if zoneListErrors.length>0}
                {#each zoneListErrors as error}
                    <IconError title={error.message} scale={0.7} on:click={()=>{
                            console.log('zoneListErrorTypes', error);
                            if (zone && error.zoneId && error.details?.markable) {
                                zoneEditor.markConflict(error.zoneId, error.details);
                            }
                    }}/>
                {/each}
                <div style="padding:0 5px;"></div>
            {/if}
        </div>
    </div>
</div>

<style>
    .vehicle {
        position: relative;
    }
    .zone-item {
        padding-right: 10px;
    }
    .zone-name {
        width: 133px;
    }
</style>
